import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Bell, BellOff, Loader2, Plus, Trash2, TrendingDown, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_TYPES = [
  { v: "price_above",  l: "Price crosses ABOVE", unit: "USD", icon: TrendingUp },
  { v: "price_below",  l: "Price drops BELOW",   unit: "USD", icon: TrendingDown },
  { v: "pct_up_24h",   l: "24h change ≥ +",      unit: "%",   icon: TrendingUp },
  { v: "pct_down_24h", l: "24h change ≤ −",      unit: "%",   icon: TrendingDown },
];

const fmtUsd = (n, dp = 2) =>
  n == null ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });

export default function CryptoPriceAlerts({ code }) {
  const [coins, setCoins] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [prices, setPrices] = useState({});
  const [waConfigured, setWaConfigured] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  // Create-form state
  const [symbol, setSymbol] = useState("BTC");
  const [alertType, setAlertType] = useState("price_above");
  const [threshold, setThreshold] = useState("");
  const [note, setNote] = useState("");

  const loadAlerts = useCallback(async () => {
    if (!code) return;
    try {
      const { data } = await axios.get(`${API}/terminal/alerts/list`, { params: { code } });
      setAlerts(data.alerts || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load alerts.");
    }
  }, [code]);

  const loadPrices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/terminal/alerts/prices`);
      const map = {};
      (data.coins || []).forEach((c) => { map[c.symbol] = c; });
      setPrices(map);
      if (!coins.length) setCoins(data.coins || []);
    } catch {}
  }, [coins.length]);

  const loadWaStatus = useCallback(async () => {
    if (!code) return;
    try {
      const { data } = await axios.get(`${API}/terminal/whatsapp/status`, { params: { code } });
      setWaConfigured(!!data.configured);
    } catch { setWaConfigured(false); }
  }, [code]);

  useEffect(() => {
    loadPrices();
    loadAlerts();
    loadWaStatus();
    const t = setInterval(loadPrices, 60_000);
    return () => clearInterval(t);
  }, [loadAlerts, loadPrices, loadWaStatus]);

  const create = async (e) => {
    e.preventDefault();
    setErr(null); setMsg(null); setBusy("create");
    try {
      await axios.post(`${API}/terminal/alerts/create`, {
        code,
        symbol,
        alert_type: alertType,
        threshold: Number(threshold),
        note,
      });
      setMsg("✓ Alert armed. You'll get a WhatsApp the moment it triggers.");
      setThreshold(""); setNote("");
      await loadAlerts();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not create the alert.");
    } finally { setBusy(null); }
  };

  const remove = async (id) => {
    setBusy(`del-${id}`); setErr(null); setMsg(null);
    try {
      await axios.post(`${API}/terminal/alerts/delete`, { code, alert_id: id });
      await loadAlerts();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not delete.");
    } finally { setBusy(null); }
  };

  const toggle = async (id, active) => {
    setBusy(`tog-${id}`); setErr(null); setMsg(null);
    try {
      await axios.post(`${API}/terminal/alerts/toggle`, { code, alert_id: id, active });
      await loadAlerts();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not update.");
    } finally { setBusy(null); }
  };

  const activeType = useMemo(() => ALERT_TYPES.find((t) => t.v === alertType), [alertType]);

  return (
    <div className="mt-8 space-y-6" data-testid="crypto-alerts-panel">
      {/* Price grid */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm uppercase tracking-[0.25em] text-gold">Live Prices · Top 10</h3>
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">refreshes every 60s</span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {coins.map((c) => {
            const p = prices[c.symbol] || c;
            const chg = p.change_24h_pct;
            const up = (chg ?? 0) >= 0;
            return (
              <button
                key={c.symbol}
                data-testid={`price-chip-${c.symbol}`}
                onClick={() => setSymbol(c.symbol)}
                className={`rounded-xl border p-3 text-left transition-colors ${
                  symbol === c.symbol
                    ? "border-gold bg-gold/5"
                    : "border-white/10 bg-white/[0.02] hover:border-white/20"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-white">{c.symbol}</span>
                  <span className={`text-[10px] ${up ? "text-emerald-400" : "text-rose-400"}`}>
                    {chg == null ? "—" : `${chg.toFixed(2)}%`}
                  </span>
                </div>
                <div className="mt-1 text-sm text-white/90">
                  ${fmtUsd(p.price_usd, p.price_usd && p.price_usd < 1 ? 4 : 2)}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/40">{c.name}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Create form */}
      <form onSubmit={create} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <h3 className="text-sm uppercase tracking-[0.25em] text-gold">New Alert</h3>
        {waConfigured === false && (
          <div
            data-testid="wa-not-configured-warning"
            className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
          >
            Register your WhatsApp number first (Account → WhatsApp alerts) so we have somewhere to deliver the ping.
          </div>
        )}

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wider text-white/50">
            Coin
            <select
              data-testid="alert-symbol-select"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              className="rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white"
            >
              {coins.map((c) => (
                <option key={c.symbol} value={c.symbol}>{c.symbol} · {c.name}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wider text-white/50">
            Trigger
            <select
              data-testid="alert-type-select"
              value={alertType}
              onChange={(e) => setAlertType(e.target.value)}
              className="rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white"
            >
              {ALERT_TYPES.map((t) => (
                <option key={t.v} value={t.v}>{t.l}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wider text-white/50">
            Value ({activeType?.unit})
            <input
              data-testid="alert-threshold-input"
              type="number"
              step="any"
              min="0"
              required
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder={activeType?.unit === "USD" ? "e.g. 72000" : "e.g. 5"}
              className="rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30"
            />
          </label>

          <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wider text-white/50">
            Note (optional)
            <input
              data-testid="alert-note-input"
              type="text"
              maxLength={140}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why does this matter to you?"
              className="rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30"
            />
          </label>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            data-testid="alert-create-btn"
            type="submit"
            disabled={busy === "create" || !threshold || waConfigured === false}
            className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-xs uppercase tracking-[0.25em] text-ink disabled:opacity-40"
          >
            {busy === "create" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Arm alert
          </button>
          {msg && <span data-testid="alert-success-msg" className="text-xs text-emerald-400">{msg}</span>}
          {err && <span data-testid="alert-error-msg" className="text-xs text-rose-400">{err}</span>}
        </div>
      </form>

      {/* Alerts list */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm uppercase tracking-[0.25em] text-gold">Your Alerts</h3>
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">
            {alerts.filter((a) => a.active).length} active · {alerts.length} total
          </span>
        </div>
        {alerts.length === 0 ? (
          <p data-testid="alerts-empty" className="mt-4 text-sm text-white/40">
            No alerts yet. Set your first one above — we'll ping your WhatsApp the second it triggers.
          </p>
        ) : (
          <ul className="mt-4 space-y-2" data-testid="alerts-list">
            {alerts.map((a) => {
              const t = ALERT_TYPES.find((x) => x.v === a.alert_type);
              const Icon = t?.icon || Bell;
              const nowPrice = prices[a.symbol]?.price_usd;
              return (
                <li
                  key={a.id}
                  data-testid={`alert-row-${a.id}`}
                  className={`flex flex-wrap items-center gap-3 rounded-xl border p-3 ${
                    a.active ? "border-white/10 bg-black/30" : "border-white/5 bg-black/10 opacity-60"
                  }`}
                >
                  <Icon className={`h-4 w-4 ${a.active ? "text-gold" : "text-white/30"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white">
                      <span className="font-semibold">{a.symbol}</span>{" "}
                      <span className="text-white/60">{t?.l || a.alert_type}</span>{" "}
                      <span className="font-mono text-white">
                        {t?.unit === "USD" ? `$${fmtUsd(a.threshold)}` : `${a.threshold}%`}
                      </span>
                      {nowPrice != null && t?.unit === "USD" && (
                        <span className="ml-2 text-[11px] text-white/40">now ${fmtUsd(nowPrice, nowPrice < 1 ? 4 : 2)}</span>
                      )}
                    </div>
                    {a.note && <div className="truncate text-[11px] text-white/40">{a.note}</div>}
                    {a.triggered_at && (
                      <div className="text-[11px] text-emerald-400/80">
                        Fired at ${fmtUsd(a.triggered_price)} · {new Date(a.triggered_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <button
                    data-testid={`alert-toggle-${a.id}`}
                    onClick={() => toggle(a.id, !a.active)}
                    disabled={busy === `tog-${a.id}`}
                    className="rounded-full border border-white/15 px-3 py-1 text-[10px] uppercase tracking-wider text-white/70 hover:border-gold hover:text-gold"
                  >
                    {busy === `tog-${a.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : (a.active ? <><BellOff className="mr-1 inline h-3 w-3" />Pause</> : <><Bell className="mr-1 inline h-3 w-3" />Resume</>)}
                  </button>
                  <button
                    data-testid={`alert-delete-${a.id}`}
                    onClick={() => remove(a.id)}
                    disabled={busy === `del-${a.id}`}
                    className="rounded-full border border-rose-500/40 px-3 py-1 text-[10px] uppercase tracking-wider text-rose-300 hover:bg-rose-500/10"
                  >
                    {busy === `del-${a.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
